import {
  Method,
  Headers,
  APIResult as OriginalAPIResult,
} from 'Common/utilities/api';
import {
  createAPIActionGenerator as originalCreateAPIActionGenerator,
  createIndirectAPIActionGenerator as originalCreateIndirectAPIActionGenerator,
  APIRequest as OriginalAPIRequest,
  APIActionGeneratorOptions,
  APIActionGenerator,
  IndirectAPIActionGeneratorOptions,
  IndirectAPIActionGenerator as OriginalIndirectAPIActionGenerator,
  IndirectAPIData as OriginalIndirectAPIData,
  APIAction as OriginalAPIAction,
  APIData as OriginalAPIData,
} from 'Common/utilities/apidata';
import {
  createPaginationAPIActionGenerator as originalCreatePaginationAPIActionGenerator,
  createIndirectPaginationAPIActionGenerator as originalCreateIndirectPaginationAPIActionGenerator,
  PaginationOptions as OriginalPaginationOptions,
  IndirectPaginationOptions as OriginalIndirectPaginationOptions,
  PaginatedAPIResult,
  PaginatedAPIRequest as OriginalPaginatedAPIRequest,
  PaginationAPIActionGenerator,
  PaginatedAPIData as OriginalPaginatedAPIData,
  IndirectPaginatedAPIData as OriginalIndirectPaginatedAPIData,
} from 'Common/utilities/pagination';
import { ErrorData, getCookieValue } from 'utils/Api';

export const APIOptions = {
  additionalHeaders: (method: Method) => {
    const headers: Headers = {};
    if (method !== 'GET') {
      // attempt to use staging cookie first. (this cookie is set only for the staging server to avoid multi domain match
      // since the prod cookie is for the root domain and staging is for subdomain)
      let cookie = getCookieValue('STAGING-XSRF-TOKEN');
      if (!cookie || cookie.length === 0) {
        cookie = getCookieValue('XSRF-TOKEN');
      }
      if (cookie) {
        headers['X-XSRF-TOKEN'] = decodeURIComponent(cookie);
      }
    }
    return headers;
  },
};

export type APIResult<T> = OriginalAPIResult<T, ErrorData>;
export type APIRequest<T> = OriginalAPIRequest<T, ErrorData>;
export type APIData<T> = OriginalAPIData<T, ErrorData>;
export type APIAction<R, T> = OriginalAPIAction<R, T, ErrorData>;

export function createAPIActionGenerator<R extends APIRequest<T>, T = unknown>(
  opts: Omit<APIActionGeneratorOptions, 'api'>
): APIActionGenerator<R, T, ErrorData> {
  return originalCreateAPIActionGenerator<R, T, ErrorData>({
    api: APIOptions,
    ...opts,
  });
}

export type IndirectAPIActionGenerator<
  R extends APIRequest<T>,
  T
> = OriginalIndirectAPIActionGenerator<R, T, ErrorData>;
export type IndirectAPIData<T = null> = OriginalIndirectAPIData<T, ErrorData>;

export function createIndirectAPIActionGenerator<
  R extends APIRequest<T>,
  T = unknown
>(opts: Omit<IndirectAPIActionGeneratorOptions<R>, 'api'>) {
  return originalCreateIndirectAPIActionGenerator<R, T, ErrorData>({
    api: APIOptions,
    ...opts,
  });
}

export type WebClientPage<P> = {
  data: P[];
  next_page_url?: string;
  previous_page_url?: string;
};

export function translatePage<P>(
  page: WebClientPage<P>
): PaginatedAPIResult<P> {
  return {
    results: page.data,
    nextPage: page.next_page_url,
    previousPage: page.previous_page_url,
  };
}

export type PaginatedAPIRequest<P> = OriginalPaginatedAPIRequest<
  WebClientPage<P>,
  ErrorData
>;

export type PaginationOptions<R extends PaginatedAPIRequest<P>, P> = Omit<
  OriginalPaginationOptions<R, WebClientPage<P>, P, ErrorData>,
  'translatePage'
>;

export function createPaginationAPIActionGenerator<
  R extends PaginatedAPIRequest<P>,
  P
>(opts: PaginationOptions<R, P>) {
  return originalCreatePaginationAPIActionGenerator<
    R,
    WebClientPage<P>,
    P,
    ErrorData
  >({
    translatePage,
    ...opts,
  });
}

export type PaginatedAPIData<P> = OriginalPaginatedAPIData<P, ErrorData>;

export type IndirectPaginationOptions<
  R extends PaginatedAPIRequest<P>,
  P
> = Omit<
  OriginalIndirectPaginationOptions<R, WebClientPage<P>, P, ErrorData>,
  'translatePage'
>;

export function createIndirectPaginationAPIActionGenerator<
  R extends PaginatedAPIRequest<P>,
  P
>(opts: IndirectPaginationOptions<R, P>) {
  return originalCreateIndirectPaginationAPIActionGenerator<
    R,
    WebClientPage<P>,
    P,
    ErrorData
  >({
    translatePage,
    ...opts,
  });
}

export type IndirectPaginatedAPIData<P> = OriginalIndirectPaginatedAPIData<
  P,
  ErrorData
>;
